import React, { useEffect, useState } from "react";
import { IconCheck, IconPlus, IconTrash, IconWarning } from "../components/ui/icons";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { Skeleton } from "../components/ui/Skeleton";
import MultiSelectDropdown from "../components/ui/MultiSelectDropdown";
import { apiFetch, unwrapArray } from "../utils/api";
import { useApiData } from "../hooks/useApiData";
import { useWarehouses } from "../hooks/useWarehouses";
import { useSkin } from "../context/SkinContext";

// Reads the server response; if it is not JSON (e.g. a PHP error as HTML), return the error text
// instead of just saying "neuspesna konekcija" - so you can see WHAT exactly failed.
async function readResponse(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    const plain = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 300);
    return { success: false, error: plain || `HTTP ${res.status}` };
  }
}

const EMPTY_SHIFT = { naziv: "", pocetak: "08:00", kraj: "16:00" };

// Message after saving: green for success, red for error, also read by screen readers (aria-live).
function SaveMessage({ text }) {
  if (!text) return <span role="status" aria-live="polite" className="sr-only" />;
  const ok = text === "Sačuvano.";
  return (
    <span
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-1.5 text-sm ${
        ok ? "text-success-dark dark:text-success-light" : "text-danger-dark dark:text-danger-light"
      }`}
    >
      {ok ? <IconCheck aria-hidden="true" /> : <IconWarning aria-hidden="true" />}
      {text}
    </span>
  );
}

export default function Settings() {
  const { data: raw, loading } = useApiData("/shifts_get.php");
  const [shifts, setShifts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const { warehouses } = useWarehouses();
  const { data: appSettingsRaw, loading: loadingAppSettings } = useApiData("/settings_get.php");
  const [defaultWarehouse, setDefaultWarehouse] = useState([]);
  const [defaultConsumptionWarehouse, setDefaultConsumptionWarehouse] = useState([]);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [defaultsStatusMsg, setDefaultsStatusMsg] = useState("");

  useEffect(() => {
    if (appSettingsRaw?.success) {
      setDefaultWarehouse(
        appSettingsRaw.default_magacin ? appSettingsRaw.default_magacin.split(",").filter(Boolean) : []
      );
      setDefaultConsumptionWarehouse(
        appSettingsRaw.default_magacin_potrosnja
          ? appSettingsRaw.default_magacin_potrosnja.split(",").filter(Boolean)
          : []
      );
    }
  }, [appSettingsRaw]);

  const handleSaveDefaults = async () => {
    setSavingDefaults(true);
    setDefaultsStatusMsg("");
    try {
      const res = await apiFetch("/settings_save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          default_magacin: defaultWarehouse.join(","),
          default_magacin_potrosnja: defaultConsumptionWarehouse.join(","),
        }),
      });
      const data = await readResponse(res);
      setDefaultsStatusMsg(data.success ? "Sačuvano." : data.error || "Greška pri čuvanju.");
    } catch {
      setDefaultsStatusMsg("Neuspešna konekcija sa serverom.");
    } finally {
      setSavingDefaults(false);
    }
  };

  useEffect(() => {
    if (raw) {
      const list = unwrapArray(raw, "smene").map((s) => ({
        naziv: s.naziv,
        pocetak: (s.pocetak || "08:00:00").slice(0, 5),
        kraj: (s.kraj || "16:00:00").slice(0, 5),
      }));
      setShifts(list);
    }
  }, [raw]);

  const updateShift = (i, field, value) => {
    setShifts((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  };

  const addShift = () => {
    if (shifts.length >= 5) return;
    setShifts((prev) => [...prev, { ...EMPTY_SHIFT }]);
  };

  const removeShift = (i) => {
    setShifts((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatusMsg("");
    try {
      const payload = {
        smene: shifts.map((s) => ({
          naziv: s.naziv,
          pocetak: `${s.pocetak}:00`,
          kraj: `${s.kraj}:00`,
        })),
      };
      const res = await apiFetch("/shifts_save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await readResponse(res);
      setStatusMsg(data.success ? "Sačuvano." : data.error || "Greška pri čuvanju.");
    } catch {
      setStatusMsg("Neuspešna konekcija sa serverom.");
    } finally {
      setSaving(false);
    }
  };

  const { skin, setSkin } = useSkin();

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <Card title="Izgled">
        <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
          Klasičan je uobičajeni izgled. Kristal je staklasti izgled sa providnim karticama; radi i u svetloj i u tamnoj
          temi. Izbor se pamti u tvom profilu.
        </p>
        <div
          role="group"
          aria-label="Izgled"
          className="inline-flex rounded-md border border-slate-300 bg-slate-100 p-0.5 dark:border-slate-700 dark:bg-slate-900"
        >
          {[
            ["klasican", "Klasičan"],
            ["kristal", "Kristal"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={skin === value}
              onClick={() => setSkin(value)}
              className={`h-8 shrink-0 whitespace-nowrap rounded px-4 text-sm font-medium transition-colors ${
                skin === value
                  ? "bg-surface text-slate-900 shadow-card dark:bg-slate-700 dark:text-white"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Podrazumevani magacin">
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Ovi magacini su unapred izabrani kad otvoriš Kontrolnu tablu, Promet artikala ili Nabavku. Polje Nabavka ima
          posebno polje jer se tamo obično koristi magacin sirovina, a ne prodajni.
        </p>
        {loadingAppSettings && <Skeleton className="h-9 w-64" />}
        {!loadingAppSettings && (
          <div className="flex flex-wrap items-end gap-4">
            <MultiSelectDropdown
              label="Grafikoni / Tabele"
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              selected={defaultWarehouse}
              onChange={setDefaultWarehouse}
            />
            <MultiSelectDropdown
              label="Nabavka"
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              selected={defaultConsumptionWarehouse}
              onChange={setDefaultConsumptionWarehouse}
            />
            <Button onClick={handleSaveDefaults} disabled={savingDefaults}>
              {savingDefaults ? "Čuvanje..." : "Sačuvaj"}
            </Button>
            <SaveMessage text={defaultsStatusMsg} />
          </div>
        )}
      </Card>

      <Card title="Radne smene">
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Definiši do 5 smena. Smena koja prelazi ponoć (npr. 22:00 do 02:00) prepoznaje se automatski: za kraj samo
          stavi vreme manje od početka. "Trenutna smena" na Kontrolnoj tabli koristi ovo da odredi otkad da broji podatke.
        </p>

        {loading && <Skeleton className="h-16 w-full rounded-lg" />}

        {!loading && (
          <div className="flex flex-col gap-3">
            {shifts.map((s, i) => (
              <div key={i} className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-800">
                <Input
                  label="Naziv"
                  value={s.naziv}
                  onChange={(e) => updateShift(i, "naziv", e.target.value)}
                  placeholder="npr. Prva smena"
                  className="w-40"
                />
                <Input label="Početak" type="time" value={s.pocetak} onChange={(e) => updateShift(i, "pocetak", e.target.value)} className="w-32" />
                <Input label="Kraj" type="time" value={s.kraj} onChange={(e) => updateShift(i, "kraj", e.target.value)} className="w-32" />
                <Button variant="danger" onClick={() => removeShift(i)} icon={IconTrash}>
                  Ukloni
                </Button>
              </div>
            ))}

            {shifts.length < 5 && (
              <Button variant="secondary" icon={IconPlus} onClick={addShift} className="self-start">
                Dodaj smenu
              </Button>
            )}

            <div className="mt-2 flex items-center gap-3">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Čuvanje..." : "Sačuvaj"}
              </Button>
              <SaveMessage text={statusMsg} />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
